import React from "react";
import { View } from "@react-pdf/renderer";
import { withNamespaces } from "react-i18next";

import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";

import styles from "../../Styles";
import { formatDisplayValue, formatPersonName, resolveCurrentUser } from "../../helpers";
import { KeyValueTable, SectionTitle, SubSectionTitle } from "../shared";
import ExternalManagementTable from "../ExternalManagementTable";
import { TOC_ANCHORS } from "../../helpers/tableOfContents";

const formatDate = (dateUtils, value) => {
    if (!value) {
        return "-";
    }
    return dateUtils.convertDateToDate(value, INTERNATIONAL_DATE_FORMAT);
};

const ExternalCompanyInformation = ({
    supplierData = {},
    user,
    downloadDate,
}) => {
    const dateUtils = new DateUtils();
    const supplierDetail = supplierData?.supplierDetail || {};
    const firstLocation = supplierDetail?.locations?.[0] || {};
    const firstShareholder = supplierData?.shareHolders?.company?.[0]
        || supplierData?.shareHolders?.individual?.[0]
        || {};
    const currentUser = resolveCurrentUser(user);

    const datasetDetails = [
        supplierData?.products?.length ? "Products" : null,
        supplierData?.contactPersons?.length ? "Contact Information" : null,
        supplierData?.teamManagement?.length ? "Management Team" : null,
        supplierData?.shareHolders?.company?.length || supplierData?.shareHolders?.individual?.length
            ? "Shareholder Information"
            : null,
        supplierData?.news?.length ? "Findings" : null,
        supplierData?.risks?.length ? "Risk Assessment" : null,
        supplierData?.tasks?.length ? "Task Management" : null,
    ].filter(Boolean).join(", ");

    const section21Rows = [
        { title: "Downloaded By", details: formatPersonName(currentUser) },
        { title: "Download On (dd/mm/yyyy; hh:mm)", details: formatDisplayValue(downloadDate) },
        { title: "Report Status Date", details: formatDate(dateUtils, supplierDetail?.lastUpdate || supplierDetail?.updateDate) },
        { title: "Dataset Details", details: datasetDetails || "-" },
    ];

    const section22Rows = [
        { title: "Company Name", details: supplierDetail?.name },
        { title: "Status", details: supplierDetail?.status },
        { title: "Primary Line of Business", details: supplierDetail?.subType },
        { title: "Establishment Location", details: [supplierDetail?.city, supplierDetail?.country].filter(Boolean).join(", ") },
        { title: "Legal Form", details: supplierDetail?.legalForm },
        { title: "Start Year", details: supplierDetail?.creationDate ? new Date(supplierDetail.creationDate).getFullYear() : "-" },
        { title: "Global Ultimate Parent", details: supplierData?.connectedLegalEntities?.[0]?.name },
        { title: "Latest Turnover", details: supplierData?.connectedLegalEntities?.[0]?.totalRevenue },
        { title: "Total Employees", details: supplierDetail?.employeesCount || supplierDetail?.employeeSize },
        { title: "Credit Rating Value", details: supplierDetail?.creditRatingValue },
        { title: "Credit Rating Description", details: supplierDetail?.highestRisk?.title },
        { title: "Credit Limit", details: supplierDetail?.creditLimit },
        { title: "Report Date", details: formatDisplayValue(downloadDate) },
    ];

    const section23Rows = [
        { title: "Registered Company Name", details: supplierDetail?.name },
        { title: "Creditsafe Number", details: supplierDetail?.creditsafeNumber },
        { title: "Language", details: supplierDetail?.language },
        { title: "Company Registration Number", details: supplierDetail?.registrationNumber },
        { title: "Business Name", details: supplierDetail?.shortName },
        { title: "Principal Activity Code", details: supplierDetail?.industries?.[0]?.class },
        { title: "Principal Activity Description", details: supplierDetail?.industries?.[0]?.name },
        { title: "Date of Registration", details: formatDate(dateUtils, supplierDetail?.creationDate) },
        { title: "Date of Starting Operations", details: formatDate(dateUtils, supplierDetail?.creationDate) },
        { title: "Is Subsidiary Location", details: supplierDetail?.isOwnCompany ? "No" : "-" },
        { title: "VAT Number", details: supplierDetail?.vatNumber },
    ];

    const section24AddressRows = [
        { title: "Address", details: firstLocation?.address },
        { title: "Postcode / City", details: [firstLocation?.postalCode, firstLocation?.city].filter(Boolean).join(" ") },
        { title: "Region / Country", details: firstLocation?.regionName || firstLocation?.countryName || supplierDetail?.country },
        { title: "Country", details: firstLocation?.countryName || supplierDetail?.country },
        { title: "Phone Number", details: supplierData?.contactPersons?.[0]?.phoneNumber },
        { title: "Deliverability", details: firstLocation?.deliverabilityStatus },
    ];

    const section26Rows = [
        { title: "Credit Rating Value", details: supplierDetail?.creditRatingValue },
        { title: "Credit Rating Description", details: supplierDetail?.highestRisk?.title },
        { title: "Credit Limit", details: supplierDetail?.creditLimit },
        { title: "Latest Shareholders Equity Figure", details: supplierDetail?.equityValue },
        { title: "Balance Sheet Currency", details: supplierDetail?.financialCurrency },
        { title: "Statement Date", details: supplierDetail?.statementDate },
    ];

    const section27Rows = [
        { title: "Ultimate Parent", details: supplierData?.connectedLegalEntities?.[0]?.name },
        { title: "Immediate Parent", details: supplierData?.connectedLegalEntities?.[1]?.name || supplierData?.connectedLegalEntities?.[0]?.name },
        { title: "Subsidiary Total Number", details: supplierData?.connectedLegalEntities?.length || 0 },
    ];

    const section28Rows = [
        { title: "Company Auditor Name", details: supplierDetail?.auditorName },
        { title: "Solicitor Name", details: supplierDetail?.solicitorName },
        { title: "Advisor Name", details: supplierDetail?.advisorName },
    ];

    const section29Rows = [
        { title: "Employee Size", details: supplierDetail?.employeeSize },
        { title: "Employees Count", details: supplierDetail?.employeesCount },
        { title: "Contact Person Count", details: supplierDetail?.contactPersonCount },
    ];

    const section210Rows = [
        { title: "Issued Share Capital", details: supplierDetail?.issuedShareCapital },
        { title: "Nominal Share Capital", details: supplierDetail?.nominalShareCapital },
    ];

    const section211Rows = [
        {
            title: "Shareholder",
            details: firstShareholder?.companyName || [firstShareholder?.title, firstShareholder?.firstName, firstShareholder?.lastName].filter(Boolean).join(" "),
        },
        { title: "Share Percent", details: firstShareholder?.percentage },
        { title: "Full Address", details: firstShareholder?.address },
        { title: "Country", details: firstShareholder?.country?.name || firstShareholder?.residenceCountry?.name },
    ];

    return (
        <View style={styles.wordSectionContent}>
            <SectionTitle title="External Company Information" number="2" anchorId={TOC_ANCHORS.SECTION_2} />

            <SubSectionTitle title="Download Details" number="2.1." anchorId={TOC_ANCHORS.SECTION_2_1} />
            <KeyValueTable rows={section21Rows} />

            <SubSectionTitle title="Executive Summary" number="2.2." anchorId={TOC_ANCHORS.SECTION_2_2} />
            <KeyValueTable rows={section22Rows} />

            <SubSectionTitle title="Company Identification & Status" number="2.3." anchorId={TOC_ANCHORS.SECTION_2_3} />
            <KeyValueTable rows={section23Rows} />

            <SubSectionTitle title="Address" number="2.4." anchorId={TOC_ANCHORS.SECTION_2_4} />
            <KeyValueTable rows={section24AddressRows} />

            <SubSectionTitle title="Management" number="2.5." anchorId={TOC_ANCHORS.SECTION_2_5} />
            <ExternalManagementTable teamManagement={supplierData?.teamManagement} />

            <SubSectionTitle title="Financial Overview" number="2.6." anchorId={TOC_ANCHORS.SECTION_2_6} />
            <KeyValueTable rows={section26Rows} />

            <SubSectionTitle title="Group Structure" number="2.7." anchorId={TOC_ANCHORS.SECTION_2_7} />
            <KeyValueTable rows={section27Rows} />

            <SubSectionTitle title="Bankers And Advisors" number="2.8." anchorId={TOC_ANCHORS.SECTION_2_8} />
            <KeyValueTable rows={section28Rows} />

            <SubSectionTitle title="Employee Information" number="2.9." anchorId={TOC_ANCHORS.SECTION_2_9} />
            <KeyValueTable rows={section29Rows} />

            <SubSectionTitle title="Share Capital Information" number="2.10." anchorId={TOC_ANCHORS.SECTION_2_10} />
            <KeyValueTable rows={section210Rows} />

            <SubSectionTitle title="Shareholder Information" number="2.11." anchorId={TOC_ANCHORS.SECTION_2_11} />
            <KeyValueTable rows={section211Rows} />
        </View>
    );
};

export default withNamespaces()(ExternalCompanyInformation);
